// =============================================================================
// Shipping Types for Shippings Page
// Data source: PostgreSQL via GET /v4/packing-slips API (V4)
// =============================================================================

/**
 * Shipping (packing slip) status values
 */
export type ShippingStatus =
  | 'to_prepare'
  | 'in_preparation'
  | 'ready'
  | 'verified'
  | 'shipped'
  | 'received';

/**
 * Shipping company values
 */
export type ShippingCompany =
  | 'canada_post'
  | 'fedex'
  | 'ups'
  | 'dhl'
  | 'purolator'
  | 'planet_courier'
  | 'atek'
  | 'other';

/**
 * V4 Shipping (Packing Slip) from PostgreSQL
 */
export interface Shipping {
  id: string;
  mongoPackingSlipId: string;
  slipNumber: string;

  // Quote reference
  mongoQuoteId: string | null;
  quoteNumber: string | null;
  quoteProjectName: string | null;

  // Status
  status: ShippingStatus;

  // Shipping information
  shippingCompany: ShippingCompany | string | null;
  trackingNumber: string | null;
  shippedDate: string | null;
  receivedDate: string | null;
  shipFrom: string | null;
  shipToSiteId: string | null;
  shipToSiteName: string | null;
  attentionTo: string | null;

  // Audit trail
  preparedById: string | null;
  preparedByName: string | null;
  preparedAt: string | null;
  verifiedById: string | null;
  verifiedByName: string | null;
  verifiedAt: string | null;
  receivedById: string | null;
  receivedByName: string | null;

  // Notes
  specialNote: string | null;
  internalNote: string | null;

  // Timestamps
  createdAt: string;
  updatedAt: string;

  // Account info (joined)
  accountId: string | null;
  accountName: string | null;
  accountNumber: string | null;
}

/**
 * Query filters for shippings list
 */
export interface ShippingsFilters {
  search?: string;
  status?: ShippingStatus;
  status_in?: ShippingStatus[];
  accountId?: string;
  shippingCompany?: ShippingCompany | string;
  pageNumber?: number;
  pageLength?: number;
  sortKey?: 'slipNumber' | 'createdAt' | 'quoteNumber' | 'status' | 'shippingCompany' | 'shippedDate';
  sortDirection?: 1 | -1;
}

/**
 * API response for shippings list
 */
export interface ShippingsResponse {
  packingSlips: Shipping[];
  pageNumber: number;
  pageLength: number;
  total: number;
}

/**
 * Canada Post tracking event
 */
export interface CanadaPostTrackingEvent {
  description: string;
  date: string | null;
  location: string | null;
  type: string;
}

/**
 * Canada Post tracking result (from official API)
 */
export interface CanadaPostTrackingResult {
  pin: string;
  serviceName: string | null;
  expectedDeliveryDate: string | null;
  actualDeliveryDate: string | null;
  events: CanadaPostTrackingEvent[];
}

// =============================================================================
// Column-level RBAC Permissions
// =============================================================================

export const SHIPPINGS_PERMISSIONS = {
  QUOTE: 'shippings.read.quote',
  SLIP_NUMBER: 'shippings.read.slip_number',
  SHIPPING_DATA: 'shippings.read.shippingData',
  STATUS: 'shippings.read.status',
} as const;

// =============================================================================
// Helpers
// =============================================================================

/**
 * Helper: Get status display info
 */
export function getStatusInfo(status: ShippingStatus): { label: string; color: string } {
  const statusMap: Record<ShippingStatus, { label: string; color: string }> = {
    to_prepare: { label: 'To Prepare', color: 'gray' },
    in_preparation: { label: 'In Preparation', color: 'yellow' },
    ready: { label: 'Ready', color: 'blue' },
    verified: { label: 'Verified', color: 'purple' },
    shipped: { label: 'Shipped', color: 'orange' },
    received: { label: 'Received', color: 'green' },
  };
  return statusMap[status] || { label: status, color: 'gray' };
}

/**
 * Helper: Get shipping company display name and whether it's a known company
 */
export function getShippingCompanyName(company: ShippingCompany | string | null): { name: string; isUnknown: boolean } {
  if (!company) return { name: '--', isUnknown: false };
  const companyMap: Record<string, string> = {
    canada_post: 'Canada Post',
    fedex: 'FedEx',
    ups: 'UPS',
    dhl: 'DHL',
    purolator: 'Purolator',
    planet_courier: 'Planet Courier',
    atek: 'ATEK',
    other: 'Other',
  };
  if (companyMap[company]) {
    return { name: companyMap[company], isUnknown: false };
  }
  return { name: company, isUnknown: true };
}

/**
 * Helper: Get shipping company display name as string (for exports)
 */
export function getShippingCompanyLabel(company: ShippingCompany | string | null): string {
  return getShippingCompanyName(company).name;
}

/**
 * Helper: Check if company is Canada Post
 */
export function isCanadaPost(company: string | null): boolean {
  return company === 'canada_post';
}

/**
 * Helper: Get Canada Post tracking URL
 */
export function getCanadaPostTrackingUrl(trackingNumber: string, lang: 'en' | 'fr' = 'en'): string {
  const cleanedNumber = trackingNumber.replace(/\s/g, '');
  return `https://www.canadapost-postescanada.ca/track-reperage/${lang}/details/${cleanedNumber}`;
}
